import { 
  BarChart3, 
  Users, 
  Menu as MenuIcon, 
  FileText, 
  Globe, 
  QrCode, 
  Clock, 
  MessageSquare, 
  ChevronRight, 
  TrendingUp, 
  MoreHorizontal, 
  Eye, 
  Calendar,
  LucideIcon 
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useState, useEffect } from 'react';
import axios from '../utils/axios';
import { format } from 'date-fns';
import { useTranslation } from 'react-i18next';

// Type Definitions
interface StatItem {
  name: string;
  value: number | string;
  icon: LucideIcon;
  color: string;
  trend: string;
  trendDirection: 'up' | 'down';
}

interface QuickAction {
  title: string;
  description: string;
  icon: LucideIcon;
  color: string;
  bgColor: string;
  borderColor: string;
  path: string;
}

interface UpcomingFeature {
  title: string;
  description: string;
  icon: LucideIcon;
  color: string;
  status: string;
}

interface RecentMenu {
  id: number;
  name: string;
  updated: string;
  views: number;
  status: 'active' | 'inactive';
  progress: number;
  restaurantName?: string;
  latestPdfUrl?: string;
  slug?: string;
}

interface DashboardStats {
  totalMenus: number;
  activeUsers: number;
  menuViews: number;
  pdfsGenerated: number;
}

interface DashboardData {
  stats: DashboardStats;
  recentMenus: RecentMenu[];
  userRole: string;
}

interface MenuApiResponse {
  success: boolean;
  data: {
    menus: Array<{
      id: number;
      title: string;
      viewCount?: number;
      pdfCount?: number;
      isActive: boolean;
      updatedAt?: string;
      createdAt: string;
      restaurant?: {
        name: string;
      };
      latestPdfUrl?: string;
      slug?: string;
    }>;
    pagination: {
      totalMenus: number;
    };
    userRole: string;
  };
}

const Dashboard = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [dashboardData, setDashboardData] = useState<DashboardData>({
    stats: {
      totalMenus: 0,
      activeUsers: 0,
      menuViews: 0,
      pdfsGenerated: 0
    },
    recentMenus: [],
    userRole: ''
  });

  const stats: StatItem[] = [
    { 
      name: t('dashboard.stats.total_menus'), 
      value: dashboardData.stats.totalMenus, 
      icon: MenuIcon, 
      color: 'bg-[#0A0C0B]',
      trend: '+2',
      trendDirection: 'up'
    },
    // { 
    //   name: t('dashboard.stats.active_users'), 
    //   value: dashboardData.stats.activeUsers, 
    //   icon: Users, 
    //   color: 'bg-[#7BD5B5]',
    //   trend: '+1',
    //   trendDirection: 'up'
    // },
  ];

  const quickActions: QuickAction[] = [
    { 
      title: t('dashboard.quick_actions.create_menu.title'), 
      description: t('dashboard.quick_actions.create_menu.description'),
      icon: MenuIcon, 
      color: 'text-[#0A0C0B]', 
      bgColor: 'bg-gradient-to-br from-white to-gray-50',
      borderColor: 'border-gray-200',
      path: '/menus/create' 
    },
    { 
      title: t('dashboard.quick_actions.manage_menu.title'), 
      description: t('dashboard.quick_actions.manage_menu.description'),
      icon: FileText, 
      color: 'text-[#7BD5B5]', 
      bgColor: 'bg-gradient-to-br from-white to-[#F0F7F4]',
      borderColor: 'border-[#7BD5B5]/20',
      path: '/menus'
    },
  ];

  const upcomingFeatures: UpcomingFeature[] = [
    {
      title: t('dashboard.upcoming_features.whatsapp_integration.title'),
      description: t('dashboard.upcoming_features.whatsapp_integration.description'),
      icon: MessageSquare,
      color: 'text-[#7BD5B5]',
      status: t('dashboard.upcoming_features.whatsapp_integration.status')
    },
    {
      title: t('dashboard.upcoming_features.google_business_sync.title'),
      description: t('dashboard.upcoming_features.google_business_sync.description'),
      icon: Globe,
      color: 'text-[#0A0C0B]',
      status: t('dashboard.upcoming_features.google_business_sync.status')
    },
    {
      title: t('dashboard.upcoming_features.dynamic_qr_codes.title'),
      description: t('dashboard.upcoming_features.dynamic_qr_codes.description'),
      icon: QrCode,
      color: 'text-[#687d76]',
      status: t('dashboard.upcoming_features.dynamic_qr_codes.status')
    },
  ];

  // Format time ago
  const formatTimeAgo = (dateString: string): string => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return t('dashboard.time_ago.just_now');
    if (diffMins < 60) return t('dashboard.time_ago.minutes_ago', { count: diffMins });
    if (diffHours < 24) return t('dashboard.time_ago.hours_ago', { count: diffHours });
    if (diffDays === 1) return t('dashboard.time_ago.yesterday');
    if (diffDays < 7) return t('dashboard.time_ago.days_ago', { count: diffDays });
    return format(date, 'MMM d, yyyy');
  };

  // Fetch dashboard data
  useEffect(() => {
    const fetchDashboardData = async () => {
      try {
        setLoading(true);
        const token = localStorage.getItem('token');
        
        // Fetch menus with pagination
        const menusResponse = await axios.get<MenuApiResponse>('/menu/all?page=1&limit=10', {
          headers: { 
            Authorization: `Bearer ${token}` 
          }
        });

        if (menusResponse.data.success) {
          const { menus, pagination, userRole } = menusResponse.data.data;
          
          // Calculate stats
          const totalMenus = pagination.totalMenus || menus.length;
          
          // You might need additional API calls for these:
          const activeUsers = 8; // This should come from another API endpoint
          const menuViews = menus.reduce((sum: number, menu) => sum + (menu.viewCount || 0), 0);
          const pdfsGenerated = menus.reduce((sum: number, menu) => sum + (menu.pdfCount || 0), 0);

          // Format recent menus
          const recentMenus: RecentMenu[] = menus.slice(0, 4).map(menu => ({
            id: menu.id,
            name: menu.title || 'Untitled Menu',
            updated: formatTimeAgo(menu.createdAt),
            views: menu.viewCount || 0,
            status: menu.isActive ? 'active' : 'inactive',
            progress: Math.min((menu.viewCount || 0) * 2, 100), // Simple progress calculation
            restaurantName: menu.restaurant?.name,
            latestPdfUrl: menu.latestPdfUrl,
            slug: menu.slug
          }));

          setDashboardData({
            stats: {
              totalMenus,
              activeUsers,
              menuViews,
              pdfsGenerated
            },
            recentMenus,
            userRole
          });
        }
      } catch (error: any) {
        console.error('Error fetching dashboard data:', error);
        setError(t('dashboard.error_message'));
      } finally {
        setLoading(false);
      }
    };

    fetchDashboardData();
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-[#7BD5B5]"></div>
          <p className="mt-4 text-[#687d76]">{t('dashboard.loading')}</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center p-8 bg-red-50 rounded-2xl border border-red-200">
          <div className="text-red-600 mb-4">
            <svg className="w-16 h-16 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
          <h2 className="text-xl font-semibold text-red-800 mb-2">{t('dashboard.error_title')}</h2>
          <p className="text-red-600 mb-4">{error}</p>
          <button 
            onClick={() => window.location.reload()}
            className="bg-red-600 text-white px-6 py-2 rounded-lg hover:bg-red-700 transition-colors"
          >
            {t('dashboard.retry')}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-[#0A0C0B]">
            {t('dashboard.title')}
          </h1>
          <p className="mt-2 text-[#687d76] text-sm sm:text-base max-w-2xl">
            {t('dashboard.welcome_back')} {dashboardData.userRole === 'admin' ? t('dashboard.welcome_admin') : t('dashboard.welcome_user')}
          </p>
        </div>
      </div>

      {/* Stats Grid - Responsive */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {stats.map((stat, index) => (
          <div 
            key={index} 
            className="bg-white rounded-xl sm:rounded-2xl border border-gray-100 p-4 sm:p-5 md:p-6 shadow-sm hover:shadow-md transition-all duration-300 hover:border-gray-200 group"
          >
            <div className="flex items-start justify-between mb-3 sm:mb-4">
              <div>
                <p className="text-xs sm:text-sm font-medium text-[#687d76] mb-1">{stat.name}</p>
                <div className="flex items-baseline gap-2">
                  <p className="text-xl sm:text-2xl md:text-3xl font-bold text-[#0A0C0B]">{stat.value}</p>
                  {stat.trend && (
                    <span className={`text-xs font-medium px-2 py-1 rounded-full flex items-center gap-1 ${
                      stat.trendDirection === 'up' 
                        ? 'bg-[#7BD5B5]/10 text-[#5a9e8a]' 
                        : 'bg-red-50 text-red-600'
                    }`}>
                      <TrendingUp className="h-3 w-3" />
                      {stat.trend}
                    </span>
                  )}
                </div>
              </div>
              <div className={`${stat.color} p-2 sm:p-2.5 md:p-3 rounded-lg sm:rounded-xl group-hover:scale-105 transition-transform`}>
                <stat.icon className="h-4 w-4 sm:h-5 sm:w-5 md:h-6 md:w-6 text-white" />
              </div>
            </div>
            <div className="mt-3 sm:mt-4 pt-3 sm:pt-4 border-t border-gray-100">
              <div className="flex items-center justify-between text-xs text-[#687d76]">
                <span>{t('dashboard.stats.this_month')}</span>
                <span className="font-medium">{t('dashboard.stats.monthly_increase')}</span>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Main Content Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 sm:gap-6">
        {/* Quick Actions - Takes 1/3 on large screens */}
        <div className="lg:col-span-1">
          <div className="bg-white rounded-xl sm:rounded-2xl border border-gray-100 p-4 sm:p-5 md:p-6 shadow-sm">
            <div className="flex items-center justify-between mb-4 sm:mb-6">
              <h2 className="text-base sm:text-lg md:text-xl font-semibold text-[#0A0C0B]">
                {t('dashboard.quick_actions.title')}
              </h2>
              <span className="text-xs text-[#687d76] bg-gray-50 px-2 py-1 sm:px-3 sm:py-1 rounded-full">
                {quickActions.length} {t('dashboard.quick_actions.title').toLowerCase()}
              </span>
            </div>
            <div className="space-y-2 sm:space-y-3">
              {quickActions.map((action, index) => (
                <button
                  key={index}
                  onClick={() => navigate(action.path)}
                  className={`w-full ${action.bgColor} ${action.borderColor} border rounded-lg sm:rounded-xl p-3 sm:p-4 text-left hover:shadow-md transition-all duration-300 hover:translate-x-1 group`}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3 sm:gap-4">
                      <div className={`p-2 sm:p-2.5 rounded-lg ${action.color} bg-white border border-gray-100 group-hover:border-gray-200`}>
                        <action.icon className="h-4 w-4 sm:h-5 sm:w-5" />
                      </div>
                      <div>
                        <h3 className="font-semibold text-[#0A0C0B] text-sm sm:text-base">{action.title}</h3>
                        <p className="text-xs sm:text-sm text-[#687d76] mt-0.5">{action.description}</p>
                      </div>
                    </div>
                    <ChevronRight className="h-4 w-4 text-gray-400 group-hover:text-[#0A0C0B] transition-colors" />
                  </div>
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Recent Menus - Takes 2/3 on large screens */}
        <div className="lg:col-span-2 space-y-4 sm:space-y-6">
          {/* Recent Menus */}
          <div className="bg-white rounded-xl sm:rounded-2xl border border-gray-100 p-4 sm:p-5 md:p-6 shadow-sm">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 sm:gap-0 mb-4 sm:mb-6">
              <div>
                <h2 className="text-base sm:text-lg md:text-xl font-semibold text-[#0A0C0B]">
                  {t('dashboard.recent_menus.title')}
                </h2>
                <p className="text-xs sm:text-sm text-[#687d76] mt-1">
                  {t('dashboard.recent_menus.subtitle')}
                </p>
              </div>
              <button 
                onClick={() => navigate('/menus')}
                className="text-sm text-[#0A0C0B] hover:text-[#2a2c2b] font-medium flex items-center gap-1 hover:gap-2 transition-all self-start sm:self-auto"
              >
                {t('dashboard.recent_menus.view_all')}
                <ChevronRight className="h-4 w-4" />
              </button>
            </div>
            <div className="space-y-3 sm:space-y-4">
              {dashboardData.recentMenus.length > 0 ? (
                dashboardData.recentMenus.map((menu) => (
                  <div 
                    key={menu.id} 
                    className="flex flex-col sm:flex-row sm:items-center justify-between p-3 sm:p-4 hover:bg-[#F8FAF9] rounded-lg sm:rounded-xl transition-colors group cursor-pointer"
                    onClick={() => navigate(`/menus/${menu.id}`)}
                  >
                    <div className="flex-1 mb-3 sm:mb-0">
                      <div className="flex flex-col sm:flex-row sm:items-center gap-3">
                        <div className="flex-1">
                          <h4 className="font-semibold text-[#0A0C0B] text-sm sm:text-base">{menu.name}</h4>
                          <p className="text-xs text-[#687d76] mt-0.5">{t('dashboard.recent_menus.updated')} {menu.updated}</p>
                        </div>
                        <span className={`px-2 py-1 sm:px-2.5 sm:py-1 rounded-full text-xs font-medium self-start sm:self-auto ${
                          menu.status === 'active' 
                            ? 'bg-[#7BD5B5]/10 text-[#5a9e8a]' 
                            : 'bg-gray-100 text-gray-800'
                        }`}>
                          {menu.status === 'active' ? t('dashboard.recent_menus.active') : t('dashboard.recent_menus.inactive')}
                        </span>
                      </div>
                      <div className="mt-3">
                        <div className="flex items-center justify-between text-xs text-[#687d76] mb-1">
                          <span className="flex items-center gap-1">
                            <Eye className="h-3 w-3" />
                            {t('dashboard.recent_menus.performance')}
                          </span>
                          <span>{menu.views} {t('dashboard.recent_menus.views')}</span>
                        </div>
                        <div className="h-1.5 bg-gray-100 rounded-full overflow-hidden">
                          <div 
                            className={`h-full rounded-full ${
                              menu.status === 'active' ? 'bg-[#7BD5B5]' : 'bg-gray-400'
                            }`}
                            style={{ width: `${menu.progress}%` }}
                          ></div>
                        </div>
                      </div>
                    </div>
                    <button 
                      onClick={(e) => {
                        e.stopPropagation();
                        navigate(`/menus/${menu.id}`);
                      }}
                      className="text-xs text-[#687d76] hover:text-[#0A0C0B] font-medium self-end sm:self-center mt-2 sm:mt-0"
                    >
                      {t('dashboard.recent_menus.view_details')}
                    </button>
                  </div>
                ))
              ) : (
                <div className="text-center py-8 sm:py-12">
                  <div className="inline-flex items-center justify-center w-12 h-12 sm:w-16 sm:h-16 bg-gray-50 rounded-full mb-3 sm:mb-4">
                    <MenuIcon className="h-6 w-6 sm:h-8 sm:w-8 text-[#7BD5B5]/40" />
                  </div>
                  <h3 className="font-semibold text-gray-700 mb-2">{t('dashboard.recent_menus.no_menus_yet')}</h3>
                  <p className="text-xs sm:text-sm text-gray-500 mb-4">
                    {t('dashboard.recent_menus.get_started')}
                  </p>
                  <button 
                    onClick={() => navigate('/menus/create')}
                    className="bg-[#0A0C0B] text-white px-4 py-2 rounded-lg hover:bg-[#1A1C1B] transition-colors text-sm"
                  >
                    {t('dashboard.recent_menus.create_first_menu')}
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

     
    </div>
  );
};

export default Dashboard;